"use client";
import { useRef } from "react";
import { gsap, ScrollTrigger, useGSAP } from "@/shared/lib/gsap";
import { useTranslations } from "next-intl";
import { useBreakpoint } from "@/shared/hooks/use-breakpoint";

// PC용 LastBanner
function LastBannerPC() {
  const bannerContainerRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const imagesWrapperRef = useRef<HTMLDivElement>(null);

  const t = useTranslations();

  useGSAP(() => {
    if (!bannerContainerRef.current) return;
    gsap.killTweensOf([bannerContainerRef.current, subtitleRef.current, titleRef.current, imagesWrapperRef.current]);

    // 이미지 래퍼에 perspective 설정
    gsap.set(imagesWrapperRef.current, {
      perspective: 300,
      transformStyle: "preserve-3d",
    });

    // 초기 상태 설정
    gsap.set([subtitleRef.current, titleRef.current], { opacity: 0, y: 50 });
    gsap.set(imagesWrapperRef.current, {
      opacity: 0,
      filter: "blur(10px)",
      scale: 0.85,
      y: 40,
      rotationX: 25,
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: bannerContainerRef.current,
        pin: true,
        start: "top top+=200",
        end: "+=700",
        scrub: 1,
      },
    });

    // 1. 서브타이틀 올라오기 (0-25%)
    tl.to(subtitleRef.current, {
      opacity: 1,
      y: 0,
      ease: "power2.out",
    })
      // 2. 메인 타이틀 올라오기 (25-50%)
      .to(titleRef.current, {
        opacity: 1,
        y: 0,
        ease: "power2.out",
      })
      // 3. 이미지 박스가 마름모에서 직사각형으로 변하면서 올라오기
      .to(imagesWrapperRef.current, {
        opacity: 1,
        filter: "blur(0px)",
        scale: 1,
        y: 0,
        rotationX: 0,
        ease: "power2.out",
        duration: 1.5,
      });
  });

  return (
    <div className="overflow-hidden">
      <div ref={bannerContainerRef} className="relative flex h-[656px] items-center justify-center">
        <div className="relative z-10 flex flex-col items-center justify-center gap-5">
          <span ref={subtitleRef} className="body-xl-medium text-gray-700">
            {t("mn_bbn_1")}
          </span>
          <h2 ref={titleRef} className="heading-2 flex flex-col text-center text-gray-900">
            <span>{t("mn_bbn_2")}</span>
            <span>
              <span className="text-primary-500">{t("mn_bbn_3")}</span>
              {t("mn_bbn_4")}
            </span>
            <span>{t("mn_bbn_5")}</span>
          </h2>

          <div ref={imagesWrapperRef} className="absolute inset-0">
            <img
              src="/images/home/mp_01.webp"
              alt="mp_01"
              width={156}
              height={158}
              className="absolute top-[-140px] left-[-56px]"
            />

            <img
              src="/images/home/mp_02.webp"
              alt="mp_02"
              width={202}
              height={204}
              className="absolute bottom-[-172px] left-[-236px]"
            />

            <img
              src="/images/home/mp_03.webp"
              alt="mp_03"
              width={324}
              height={272}
              className="absolute right-[-344px] bottom-[-17px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// 모바일용 LastBanner (아직 디자인 안됨 - 대기 상태)
function LastBannerMobile() {
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isTablet } = useBreakpoint();

  useGSAP(
    () => {
      gsap.killTweensOf([containerRef.current]);

      ScrollTrigger.create({
        trigger: containerRef.current,
        scrub: 1,
        start: "top center",
        end: "bottom center",
        onEnter: () => {
          // 스크롤 아래로: 나타나는 애니메이션
          gsap.to(".last-banner-text", {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
          });

          gsap.to(".last-banner-images", {
            opacity: 1,
            filter: "blur(0px)",
            scale: 1,
            duration: 0.8,
            ease: "power2.out",
          });

          gsap.to(".last-banner-images img", {
            filter: "blur(0px)",
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: "power2.out",
          });
        },

        onLeaveBack: () => {
          // 스크롤 위로 벗어남: 블러 상태로 되돌림
          gsap.to(".last-banner-text", {
            opacity: 0,
            y: 30,
            duration: 0.3,
            ease: "power2.in",
          });

          gsap.to(".last-banner-images", {
            opacity: 0,
            filter: "blur(40px)",
            scale: 0.8,
            duration: 0.3,
            ease: "power2.in",
          });

          gsap.to(".last-banner-images img", {
            filter: "blur(40px)",
            scale: 0.9,
            duration: 0.3,
            ease: "power2.in",
          });
        },
      });
    },
    { scope: containerRef },
  );

  return (
    <div
      ref={containerRef}
      className="tb:px-10 tb:py-55 relative flex items-center justify-center px-4 pt-46.5 pb-37.5"
    >
      {/* 초기 상태: opacity-0으로 숨김 */}
      <div className="last-banner-text tb:gap-5 flex flex-col gap-3 text-center opacity-0">
        <span className="body-xl-medium tb:whitespace-normal whitespace-pre-line text-gray-700">{t("mn_bbn_1")}</span>
        {isTablet ? (
          <h2 className="heading-2 text-gray-900">
            <span>{t("mn_bbn_2")}</span>
            <br />
            <span className="whitespace-pre-line">
              <span className="text-primary-500">{t("mn_bbn_3")}</span>
              {t("mn_bbn_4")}
            </span>
            <br />
            <span>{t("mn_bbn_5")}</span>
          </h2>
        ) : (
          <h2 className="heading-2 text-gray-900">
            <span>{t("mn_bbn_2")}</span>
            <span>
              <span className="text-primary-500">{t("mn_bbn_3")}</span>
              {t("mn_bbn_4")}
            </span>
            <span>{t("mn_bbn_5")}</span>
          </h2>
        )}
      </div>

      {/* 초기 상태: opacity-0으로 숨김 */}
      <div className="last-banner-images absolute inset-0 opacity-0">
        <img
          src="/images/home/mp_01.webp"
          alt="mp_01"
          className="tb:size-30 tb:top-[58px] tb:left-[58px] absolute top-[35px] left-[30px] size-20 blur-lg"
        />
        <img
          src="/images/home/mp_02.webp"
          alt="mp_02"
          className="tb:left-7 tb:bottom-5 tb:size-[159px] absolute bottom-[22px] left-2 size-25 blur-lg"
        />
        <img
          src="/images/home/mp_03.webp"
          alt="mp_03"
          className="tb:w-[265px] tb:h-[219px] tb:top-0 tb:right-[-65px] absolute top-[81px] right-[-28px] h-[118px] w-[140px] blur-lg"
        />
      </div>
    </div>
  );
}

export function LastBanner() {
  const { isPc } = useBreakpoint();

  return isPc ? <LastBannerPC /> : <LastBannerMobile />;
}
